import Session from "@lib/session";
import Cookies from "js-cookie";
import router from "next/router";
import {
  LoginProfileDocument,
  LoginProfileMutationResult,
  LoginProfileMutationVariables,
  UserProfileDocument,
} from "@graphql/api";
import logoutProfile from "./logout_profile";

type ProfileType = "CHARTER" | "COUPON" | "SALES" | "USER" | "GROUP";

export default async function loginProfile({
  id,
  password,
  type,
}: {
  id: string;
  password?: string;
  type: ProfileType;
}): Promise<{ actualType?: string; error?: string; success?: boolean }> {
  const client = global.window.apolloClient;

  // Validate profile
  const mutVars: LoginProfileMutationVariables = { id, password, type };
  const result: LoginProfileMutationResult = await client.mutate({
    mutation: LoginProfileDocument,
    variables: mutVars,
  });
  const loginProfileResult = result.data?.loginProfile || {};

  // If error
  if (loginProfileResult.error) {
    if (type == "GROUP") Cookies.remove(`b_${router.query.siteId}_gi`);
    return { error: loginProfileResult.error, success: false };
  }

  // If successful
  // Add group to the booking session
  // The returned profile could be a different type then what was expected.
  // (ex. requested a group but got back a coupon).
  // In this case we want to treat the profile correctly so we need to use the "actual" type from now on.
  const actualType = loginProfileResult.type || type;
  type ProfilesKey = keyof be.Profiles;
  const profileKeyMap: { [key: string]: ProfilesKey } = {
    CHARTER: "charter",
    COUPON: "coupon",
    SALES: "sales",
    USER: "user",
    GROUP: "group",
  };
  const profileKey: ProfilesKey | undefined = profileKeyMap[actualType];
  if (profileKey) {
    let updatedProfiles: be.Profiles = { [profileKey]: loginProfileResult.id };
    if (profileKey == "coupon") updatedProfiles[`couponWeb`] = id;

    if ((profileKey == "user" || profileKey == "charter") && loginProfileResult.attachedGroup) {
      updatedProfiles.group = loginProfileResult.attachedGroup;
    }

    await setupProfiles(updatedProfiles);
  }

  return { success: true, actualType };
}

interface SetupProfilesParams {
  charter?: number;
  coupon?: number;
  sales?: number;
  user?: number | null;
  group?: number | null;
}

// Run this function when we've validated that a user/group is logged in. This will
// load their profile details into the booking session and perform any other setup needed.
export async function setupProfiles(profiles: SetupProfilesParams) {
  const updatedProfiles = { ...Session.getBooking().profiles, ...profiles };

  // Groups and coupons are the same thing in the backend.
  // Only allow one to exist at the same time.
  if (profiles.coupon) {
    delete updatedProfiles.group;
  } else if (profiles.group) {
    delete updatedProfiles.coupon;
    delete updatedProfiles.couponWeb;
  }
  Session.updateBooking({ profiles: updatedProfiles });

  // If this is a charter profile we can't have a user logged in.
  if (profiles.charter && updatedProfiles.user) await logoutProfile("USER");

  // Keep the group in a cookie so that SSR can use it. Back-end should always validate it against
  // the JWT before returning any real data since a bad user could mess with it.
  if (updatedProfiles.group)
    Cookies.set(`b_${router.query.siteId}_gi`, updatedProfiles.group.toString());

  // Reload the Apollo cache.
  const client = global.window.apolloClient;
  await client.resetStore();

  // If this was a user login let's preload their profile details.
  if (profiles.user) {
    let { data, loading } = await client.query({
      query: UserProfileDocument,
      variables: { id: Number(profiles.user) },
    });
  }
}
